<template>
    <article>
        <div v-if="notFound" class="not-found-wrapper">
             <NotFound />
        </div>
        <div v-else-if="pathname" class="article-content" id="pdf-content">
            <div class="left-pane">
                <div v-for="(item, index) in chartdata.feetype" class="dataContainer" :key="item.name">
                    <!-- Title and description on every page -->
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px;">
                    <div class="pdf-page-header"><h1 v-if="title">{{ title }}</h1></div>
                    <h1 v-if="title && index === 0" class="web-only-title">{{ title }}</h1>
                        <img :src="pdfLogoUrl" class="pdf-logo" alt="LCP Logo" width="125" height="44" />
                        <button v-if="PDF_DOWNLOAD_ENABLED && index === 0" @click="downloadPDF" class="pdf-btn" data-html2canvas-ignore="true">
                            Download PDF
                        </button>
                    </div>
                    <div v-if="chartdata.description" class="description-top">
                        <div class="fig-text-wrapper">
                          {{ capitalize(chartdata.description) }}
                        </div>
                    </div>
                    <p v-if="chartdata.onlyMedianQuartiles" class="disclaimer-text">
                      We have only shown a median fee range for asset classes with lower response rates.
                    </p>
                    <charts :options="item" class="chartContainer"></charts>
                    <fee-table :name="item.name"
                               :tableheader="item.tableheader"
                               :tablerows="item.tablerows"
                               :formatTableNumber="formatTableNumber" />
                    <div class="pdf-page-footer">This information is wholly owned by Lane, Clark &amp; Peacock LLP.</div>
                    <div v-if="index < chartdata.feetype.length - 1" class="html2pdf__page-break"></div>
                </div>
            </div>
            <div v-if="chartdata.description" class="right-pane">
                <div class="fig-text-wrapper">
                  {{ chartdata.description }}
                </div>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">

    import { ref, watch, onMounted, onServerPrefetch, computed } from 'vue'
    import axios from 'axios'
    import { useRoute } from 'vue-router'
    import { useHead } from '@unhead/vue'
    import FeeTable from '../components/fee-table.vue'
    import NotFound from './not-found.vue'
    import { useYear } from '../composables/useYear'
    import { BASE_URL, PDF_DOWNLOAD_ENABLED } from '../config'

    // Types
    type ChartSeries = {
      name?: string
      data: number[] | Array<[number, number]>
      color?: string
      lineWidth?: number
      type?: string
      showInLegend?: boolean
      zIndex?: number
    }

    type FeeType = {
      name: string
      tableheader: string
      tablerows: { rowdata: (string | number)[] }[]
      series: ChartSeries[]
      yAxis?: YAxisOptions
    }

    type ChartData = {
      feetype: FeeType[]
      description: string
      name: string
      tickinterval: number
      onlyMedianQuartiles?: boolean
    }

    type ChartWrapper = {
        id: number
        category: string
        name: string
        pathname: string
        description: string
        exclude?: boolean
        onlyMedianQuartiles?: boolean
        chart: ChartData
    }

    // YAxis type definition
    type YAxisOptions = {
      min?: number
      labels?: {
        formatter?: (this: { value: number }) => string
        style?: { color?: string }
        align?: string
        y?: number
        x?: number
      }
      tickInterval?: number
      plotLines?: Array<{ value: number; width: number; color: string }>
      title?: { text?: string; style?: { color?: string }; offset?: number; margin?: number }
    }

    type PdfDocument = {
      internal: {
        getNumberOfPages: () => number
        pageSize: {
          getWidth: () => number
          getHeight: () => number
        }
      }
      setPage: (pageNumber: number) => void
      setDrawColor: (red: number, green: number, blue: number) => void
      setLineWidth: (width: number) => void
      line: (x1: number, y1: number, x2: number, y2: number) => void
      setFontSize: (size: number) => void
      setTextColor: (red: number, green: number, blue: number) => void
      text: (text: string, x: number, y: number, options?: { align?: string }) => void
    }

    // Props and State
    import logoUrl from '@/assets/logo_sector.svg'
    const props = defineProps<{ pathname?: string }>()
    const chartdata = ref<ChartData>({ feetype: [], description: '', name: '', tickinterval: 0.1 })
    const title = ref('')
    const colors = ['#00a5c9', '#fba61c', '#17355e', 'rgba(0,165,201,0.2)', 'rgba(23,53,94,0.2)']
    const notFound = ref(false)
    const isPdfExporting = ref(false)
    const pdfLogoUrl = ref(logoUrl)
    const pdfLogoPngCache = ref<string | null>(null)

    // Caching for aggregated chart data (Year -> Data)
    // Defined outside the component to persist across navigations
    const chartsCache: Record<string, ChartWrapper[]> = {}

    const { selectedYear } = useYear()
    const route = useRoute()

    // Chart configuration
    const commonChartOptions = {
      chart: { marginTop: 20, spacingBottom: 0 }, // Default margin, auto layout for bottom
      title: { text: '', x: 0 },
      xAxis: {
        categories: ['£25mn', '£50mn', '£75mn', '£100mn', '£125mn', '£150mn', '£175mn', '£200mn', '£225mn', '£250mn','£275mn', '£300mn', '£325mn', '£350mn', '£375mn', '£400mn', '£425mn', '£450mn', '£475mn', '£500mn'],
        title: { text: 'Mandate size', style: { color: '#17355e' } },
        labels: { style: { color: '#17355e' } },
        tickLength: 0
      },
      yAxis: {
        min: 0,
        title: { offset: 5, margin: 0 },
        labels: {
          formatter(this: { value: number }) {
            return `${parseFloat(this.value.toString()).toFixed(2)}%`
          },
          style: { color: '#17355e' },
          align: 'left',
          y: -4,
          x: 0
        },
        tickInterval: 0.1,
        plotLines: [{ value: 0, width: 1, color: '#17355e' }]
      },
      plotOptions: {
        line: { marker: { symbol: 'circle', radius: 6 } },
        series: {  }
      },
      tooltip: { valueSuffix: '%', valueDecimals: 2 },
      legend: { enabled: true, layout: 'horizontal', align: 'center', verticalAlign: 'bottom' },
      credits: { enabled: false }
    }

    const getChartOptions = () => ({
      ...commonChartOptions,
      chart: {
        ...commonChartOptions.chart
      },
      yAxis: {
        ...commonChartOptions.yAxis,
      }
    })

    // Utils
    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

    const formatTableNumber = (value: string | number) => {
      const num = Math.round(Number(value) / 1000)
      return isNaN(num) ? '-' : num.toLocaleString()
    }

    const applySeriesStyle = (series: ChartSeries, index: number) => ({
      ...series,
      color: colors[index],
      lineWidth: 4,
      type: 'line',
      showInLegend: true,
      zIndex: 0
    })

    const createAreaSeries = (index: number, data: Array<[number, number]> = []) => ({
      data,
      type: 'arearange',
      color: colors[0],
      fillColor: colors[index],
      showInLegend: false,
      zIndex: -5,
      lineWidth: 0
    })

    const processSeriesData = (seriesData: ChartSeries[], onlyMedianQuartiles: boolean = false) => {
      // If onlyMedianQuartiles is true, filter for median series only
      if (onlyMedianQuartiles) {
        const medianSeries = seriesData.find(s => s.name?.toLowerCase().includes('median'))
        if (medianSeries) {
          // Return only median series, styled using index 1 which corresponds to the median color in original logic
          return [applySeriesStyle(medianSeries, 1)]
        }
        // Fallback if no median found: return original data processed normally
      }

      // Standard processing
      // Style the original lines
      const styledSeries = seriesData.map((s, i) => applySeriesStyle(s, i))

      // Extract raw data for calculations
      const [data0, data1, data2] = styledSeries.map(s => s.data as number[])

      // Create area ranges
      const topRange = createAreaSeries(3)
      const medianRange = createAreaSeries(4)

      // Fill with calculated data
      data0.forEach((_, i) => {
        topRange.data.push([data1[i], data0[i]])
        medianRange.data.push([data2[i], data1[i]])
      })

      return [...styledSeries, medianRange, topRange]
    }

    const processTableData = (rows: { rowdata: (string | number)[] }[], onlyMedianQuartiles: boolean = false) => {
      if (!onlyMedianQuartiles) return rows

      return rows.filter((row, index) => {
        // Keep the header row (assumed to be index 0 or purely data rows? Looking at JSON, index 0 is empty header row with categories)
        // Checking JSON: tablerows[0] is header row (years/sizes), subsequent rows are data.
        if (index === 0) return true

        // Filter other rows based on name in first column
        const rowName = String(row.rowdata[0]).toLowerCase()
        return rowName.includes('median')
      })
    }



    // Data fetching
    const fetchChartData = async () => {
      // Sync global year with route year
      const routeYear = route.params.year as string
      if (routeYear && routeYear !== selectedYear.value) {
          selectedYear.value = routeYear
      }

      // Reset not found state
      notFound.value = false

      if (!props.pathname) return

      const yearKey = routeYear || selectedYear.value

      try {
        if (!chartsCache[yearKey]) {
             // Fetch via axios for both SSR and Client (files are static in public/)
             // Note: In SSG, localhost request might fail if server isn't running, but we are building static files.
             // For SSG specific data loading, we might need a different strategy if axios fails,
             // but public/ imports are strictly forbidden.
             // Given we are in SSG, we can try to use standard fetch or fs in vite.config,
             // but here in component, we must use URL fetch.
             try {
                 const { data } = await axios.get(`${BASE_URL}/data/data_${yearKey}/all_charts.json`);
                 chartsCache[yearKey] = data;
             } catch (e) {
                 console.warn(`[Chart] Failed to load data for ${yearKey}`, e);
             }
        }

        // 2. Find the specific chart
        if (!Array.isArray(chartsCache[yearKey])) {
            console.warn(`[Chart] Data for ${yearKey} is invalid (not an array).`);
            notFound.value = true;
            return;
        }

        const found = chartsCache[yearKey]?.find((item: ChartWrapper) => item.pathname === props.pathname)

        if (!found) {
            // Chart not found - set state to render 404 component
            notFound.value = true
            return
        }

        if (found.exclude) {
             console.warn(`Chart pathname ${props.pathname} is excluded.`);
             notFound.value = true
             return;
        }

        // Data Structure Update:
        // 'found.chart' no longer contains name/description. We must inject them from the wrapper.
        const data = JSON.parse(JSON.stringify(found.chart)) // Clone to avoid mutating cache reference

        // 3. Metadata Injection
        // Use metadata from the wrapper (which came from charts.json via build script)
        if (found.description) {
            data.description = found.description;
        }

        if (found.name) {
             data.name = found.name;
        }

        if (found.onlyMedianQuartiles) {
            data.onlyMedianQuartiles = found.onlyMedianQuartiles;
        }

        chartdata.value = data
        title.value = data.name

        // Update Document Title separately (Client Only)
        if (typeof document !== 'undefined') {
            document.title = data.name;
            window.dataLayer?.push?.({
                event: 'virtualPageview',
                pageUrl: BASE_URL + (props.pathname ? `/${yearKey}/chart/${props.pathname}` : window.location.pathname),
                pageTitle: document.title
            });
        }

        chartdata.value.feetype.forEach((item: FeeType) => {
          // Apply chart options based on type
          const options = getChartOptions()
          Object.assign(item, options)

          // Set tick interval from data or default
          const tickInterval = chartdata.value.tickinterval ?? 0.1
          item.yAxis = { ...(item.yAxis || {}), tickInterval }

          // Process chart series
          item.series = processSeriesData(item.series, chartdata.value.onlyMedianQuartiles)

          // Process table data
          item.tablerows = processTableData(item.tablerows, chartdata.value.onlyMedianQuartiles)
        })
      } catch (error) {
        console.error('Failed to fetch chart data', error)
        notFound.value = true
      }
    }

    // Lifecycle
    onMounted(() => {
      fetchChartData()
    })

    onServerPrefetch(async () => {
        await fetchChartData()
    })

    // Watch for prop changes and route year changes
    watch(() => [props.pathname, route.params.year], () => fetchChartData())

    // Dynamic SEO for Charts


    const pageTitle = computed(() => title.value || 'Chart')
    const pageDescription = computed(() => chartdata.value.description || 'Investment fee data analysis')
  const pdfFooterText = 'This information is wholly owned by Lane, Clark & Peacock LLP.'

    // PDF Export
    const getPdfLogoSrc = async () => {
      if (pdfLogoPngCache.value) {
        return pdfLogoPngCache.value
      }

      return new Promise<string>((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'

        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.naturalWidth || 125
          canvas.height = img.naturalHeight || 44

          const ctx = canvas.getContext('2d')
          if (!ctx) {
            resolve(logoUrl)
            return
          }

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          const pngDataUrl = canvas.toDataURL('image/png')
          pdfLogoPngCache.value = pngDataUrl
          resolve(pngDataUrl)
        }

        img.onerror = () => resolve(logoUrl)
        img.src = logoUrl
      })
    }

    const addPdfFooters = (pdf: PdfDocument) => {
      const pageCount = pdf.internal.getNumberOfPages()
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const horizontalInset = 10
      const textY = pageHeight - 12
      const lineY = textY - 3

      for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        pdf.setPage(pageNumber)
        pdf.setDrawColor(204, 204, 204)
        pdf.setLineWidth(0.2)
        pdf.line(horizontalInset, lineY, pageWidth - horizontalInset, lineY)
        pdf.setFontSize(7)
        pdf.setTextColor(102, 102, 102)
        pdf.text(pdfFooterText, pageWidth / 2, textY, { align: 'center' })
      }
    }

    const downloadPDF = async () => {
        const element = document.getElementById('pdf-content');
        if (!element) return;

      pdfLogoUrl.value = await getPdfLogoSrc()
        isPdfExporting.value = true
        // Add class to force mobile/single-column layout for PDF
        element.classList.add('pdf-mode');

        // Wait for fonts and highcharts redraw
        await Promise.all([
          document.fonts.ready,
          new Promise(r => setTimeout(r, 1000))
        ]);

        const options = {
            margin:       10,
            filename:     `${title.value ? title.value.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 'chart'}_report.pdf`,
            image:        { type: 'jpeg' as const, quality: 0.98 },
            html2canvas:  { scale: 2, useCORS: true, windowWidth: 800 },
            jsPDF:        { unit: 'mm' as const, format: 'a4' as const, orientation: 'landscape' as const },
            pagebreak:    { mode: ['avoid-all', 'css', 'legacy'] }
        };

          const { default: html2pdf } = await import('html2pdf.js');
          const worker = html2pdf().set(options).from(element).toPdf();
          const pdf = await worker.get('pdf');
          addPdfFooters(pdf);
          await worker.save();

        // Remove the class to restore normal layout
        isPdfExporting.value = false
        pdfLogoUrl.value = logoUrl
        element.classList.remove('pdf-mode');
    }

    useHead({
        title: pageTitle,
        meta: [
            { name: 'description', content: pageDescription },
            { property: 'og:title', content: () => `Fee Survey: ${pageTitle.value}` },
            { property: 'og:description', content: pageDescription },
            { property: 'og:image', content: `${BASE_URL}/images/ifs-social.png` },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: () => `Fee Survey: ${pageTitle.value}` },
            { name: 'twitter:description', content: pageDescription },
            { name: 'twitter:image', content: `${BASE_URL}/images/ifs-social.png` }
        ],
        script: [
            {
                type: 'application/ld+json',
                innerHTML: computed(() => JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Dataset",
                    "name": `LCP Fee Survey - ${pageTitle.value}`,
                    "description": pageDescription.value,
                    "license": "https://feesurvey.lcp.com/terms-and-conditions",
                    "creator": {
                        "@type": "Organization",
                        "name": "Lane Clark & Peacock LLP",
                        "url": "https://www.lcp.com"
                    },
                    "provider": {
                        "@type": "Organization",
                        "name": "Lane Clark & Peacock LLP"
                    },
                    "variableMeasured": "Investment Fees"
                }))
            }
        ]
    })
</script>

<style lang="scss">
    @media only screen and (max-width: 1024px) {
      .highcharts-container, .highcharts-root {
        width: 100% !important;
      }
    }
</style>

<style lang="scss" scoped>
    .pdf-logo {
      display: none;
    }

    .pdf-btn {
      background-color: var(--lcp-blue, #17355e);
      color: white;
      border: none;
      border-radius: 5px;
      padding: 8px 16px;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--investment-violet, #00a5c9);
      }
    }

    // Description shown at top only during PDF mode
    .description-top {
      display: none;
      margin-bottom: 20px;
    }

    // PDF mode: force single-column mobile layout
    .pdf-mode {
      display: block !important;
      overflow: hidden !important;
      font-family: var(--montserrat) !important;

      .pdf-logo {
        display: block !important;
        max-width: 125px !important;
        height: auto !important;
        flex-shrink: 0 !important;
      }

      .left-pane, .right-pane {
        width: 100% !important;
        padding: 0 !important;
      }

      // Hide the sidebar description (it's duplicated at the top)
      .right-pane {
        display: none !important;
      }

      // Show the top description instead
      .description-top {
        display: block !important;
      }

      // Body font size for PDF
      font-size: 10pt !important;
      line-height: 12.5pt !important;
      margin-bottom: 4pt !important;

      p {
        font-size: 10pt !important;
        line-height: 12.5pt !important;
        margin-top: 0 !important;
        margin-bottom: 4pt !important;
      }

      h1 {
        font-size: 18pt !important;
        line-height: 22pt !important;
        margin-top: 0 !important;
        margin-bottom: 12pt !important;
        padding-bottom: 12pt !important;
        position: relative !important;
        display: inline-block !important;

        &::before, &::after {
          display: block !important;
        }
      }

      h2 {
        font-size: 14pt !important;
        line-height: 18pt !important;
        margin-top: 0 !important;
        margin-bottom: 5px !important;
      }

      .disclaimer-text {
        margin-bottom: 5px !important;
      }

      // Description styling in PDF
      .fig-text-wrapper, .description-top .fig-text-wrapper {
        font-size: 10pt !important;
        padding-left: 0 !important;

        &::before {
          display: none !important;
        }

        &::first-letter {
          text-transform: uppercase !important;
        }
      }

      .chartContainer {
        width: 1000px !important;
        min-height: 400px !important;
        padding: 0 !important;
        margin: 8pt 0 8pt 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
      }

      .dataContainer {
        margin: 0 !important;
        padding: 0 !important;
        min-height: 180mm !important;
        /*display: flex !important;
        flex-direction: column !important;*/
        position: relative !important;
        break-inside: avoid-page !important;
        page-break-inside: avoid !important;
      }

      &.article-content {
        padding: 10px !important;
      }

      :deep(.tableArea) {
        margin: 0 !important;
        padding: 0 !important;
        overflow-x: visible !important;
      }

      // Increase chart height for readability
      :deep(.highcharts-container),
      :deep(.highcharts-root) {
        height: 400px !important;
      }

      // Remove internal Highcharts spacing if possible via CSS
      :deep(.highcharts-root) {
        padding: 0 !important;
        margin: 0 !important;
      }

      // Shrink table font so all columns fit on A4 landscape
      :deep(td),
      :deep(th) {
        font-size: 0.55rem !important;
        padding: 2px 2px !important;
      }

      :deep(td:first-child),
      :deep(th:first-child) {
        min-width: auto !important;
      }

      // Force white background on tables
      :deep(tr),
      :deep(tbody tr td:first-child),
      :deep(thead tr th:first-child) {
        background-color: #ffffff !important;
      }

      // Hide mobile scroll hint in PDF
      :deep(.mobile-scroll-hint) {
        display: none !important;
      }

      // Show per-page heading
      .pdf-page-header {
        display: block !important;
      }

      // Hide web-only title in PDF
      .web-only-title {
        display: none !important;
      }

      // Show per-page footer
      .pdf-page-footer {
        display: none !important;
      }
    }

    // Hidden by default (only shown in pdf-mode)
    .pdf-page-header {
      display: none;

      h1 {
        margin: 0;
      }
    }

    .web-only-title {
      display: inline-block;
    }

    .pdf-page-footer {
      display: none;
      padding-top: 5px !important;
      border-top: 1px solid #ccc;
      font-size: 0.65rem;
      color: #666;
      text-align: center;
    }

    .html2pdf__page-break {
      page-break-after: always;
    }

    // Layout
    .article-content {
      display: flex;
      flex-wrap: wrap;
      padding: 25px;
    }

    .left-pane {
      padding-right: 20px;
      width: 75%;
    }

    .right-pane {
      width: 25%;
    }

    // Chart and data containers
    .dataContainer {
      margin: 0 0 60px 0;


      &:last-child {
        margin-bottom: 0;
      }
    }

    .chartContainer {
      margin: 0;
      border-radius: 20px;
      padding: 20px;
      background-color: #fff;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    // Typography
    h1 {
      font-size: 1.5rem;
      font-weight: 700;
      padding: 0 0 20px 0;
      color: var(--lcp-blue);
      position: relative;
      margin-bottom: 20px;
      display: inline-block; // Makes the element only as wide as its content

      // Horizontal underline with fade - responsive to text width
      &::before {
        content: '';
        position: absolute;
        bottom: 8px;
        left: 0;
        width: calc(100% - 10px); // Text width plus extra 30px
        height: 3px;
        background: linear-gradient(to right,
          var(--investment-violet) 0%,
          var(--investment-violet) 50%,
          transparent 100%);
        border-radius: 2px;
      }

      // Small accent dot at start
      &::after {
        content: '';
        position: absolute;
        z-index: 0;
        bottom: 5px;
        left: 0px;
        width: 9px;
        height: 9px;
        background: var(--investment-violet);
        border-radius: 50%;

      }
    }

    .disclaimer-text {
      font-style: italic;
      margin-bottom: 20px;
    }

    h3 {
      font-size: 1.1rem;
      font-weight: 700;
    }

    // Figure caption
    figcaption {
      display: flex;
      align-items: flex-start;
      position: relative;
      padding: 0 0 0 20px;
    }

    .fig-text-wrapper {
      color: var(--lcp-blue);
      position: relative;
      padding: 0 0 0 20px;

      &::first-letter {
        text-transform: uppercase;
      }

      &::before{
          font-family: var(--font-awesome-font);
          font-weight: 900;
          content: "\2b";
          display: inline-block;
          text-rendering: auto;
          -webkit-font-smoothing: antialiased;
          font-size: 1.4rem;
          position: absolute;
          left: 0px;
          top: -13px;
          color: var(--investment-violet);
      }
    }

    // Responsive styles
    @mixin responsive-layout {
      .left-pane, .right-pane {
        width: 100% !important;
        padding: 0;
      }

      .right-pane {
        margin-top: 20px;
      }

      .article-content {
        width: 100% !important;
        padding: 15px;
        display: block;
        margin: 0;
        overflow: hidden;
      }

      .chartContainer, .highcharts-container {
        width: 100% !important;
      }
    }

    @media only screen and (min-width: 821px) and (max-width: 1024px) {
      .article-content {
        display: block;
      }
      .left-pane {
        padding-right: 0;
        width: 100%;
      }
      .right-pane {
        padding-top: 20px;
        width: 100%;
      }
    }

    @media only screen and (max-width: 820px) {
      article {
        min-width: 100%;
        display: block;
        overflow-y: scroll;
        -webkit-overflow-scrolling: touch;
      }

      @include responsive-layout;

      .fa-plus {
        min-width: 25px;
        padding: 3px 8px 0 0;
      }

      .right-pane {
        padding-top: 20px;
        width: 100%;
      }
            .tableArea {
        overflow-x: auto;
      }

      h3, .table {
        width: 100%;
        margin-left: 0;
      }

      .left-menu {
        display: block;
        position: absolute;
        left: -1000px;
        z-index: 100;

        &.active {
          left: 0;
        }
      }
    }
</style>
